import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomField,
  CustomFieldsQuery,
} from "@/Features/CustomFields";
import LocationsAPI from "@/api/locations";
import { LocationsAPI as LocationsMetaAPI } from "@/Features/Locations/locations.api";
import { StorageAPI } from "../storage.api";

export const StorageFilterDefinitions: FilterDefinition[] = [
  {
    label: "Storage Number",
    dataField: "storage_number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  // Date filters expose only After/Before/Between (like the legacy page):
  // the resolver runs raw `= ?` against full ISO timestamps, so day-resolution
  // Equals/NotEquals would silently match nothing/everything.
  {
    label: "Created On",
    dataField: "created_on",
    inputType: "datetime",
    resolution: "day",
    operators: [Operators.GreaterThan, Operators.LessThan, Operators.Between],
  },
  {
    label: "Last Audit Date",
    dataField: "last_audit_date",
    inputType: "datetime",
    resolution: "day",
    operators: [Operators.GreaterThan, Operators.LessThan, Operators.Between],
  },
  {
    label: "Date Assigned",
    dataField: "date_assigned",
    inputType: "datetime",
    resolution: "day",
    operators: [Operators.GreaterThan, Operators.LessThan, Operators.Between],
  },
  {
    label: "Location Path",
    dataField: "location_path",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "UUID",
    dataField: "uuid",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "General Storage",
    pluralLabel: "General Storage",
    dataField: "general_storage",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    selectOptions: [
      { label: "Yes", value: "1" },
      { label: "No", value: "0" },
    ],
  },
  {
    label: "Type",
    pluralLabel: "Types",
    dataField: "type",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      cacheKey: "storage:types",
      loadPage: async () => {
        const items = (await StorageAPI.getTypes()) as { type: string }[];
        return {
          items: (items || []).map((item) => ({
            label: item.type,
            value: item.type,
          })),
          nextPageParam: null,
        };
      },
    },
  },
  {
    label: "Provider",
    pluralLabel: "Providers",
    dataField: "make",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      cacheKey: "storage:providers",
      loadPage: async () => {
        const items = (await StorageAPI.getProviders()) as {
          make: string;
        }[];
        return {
          items: (items || []).map((item) => ({
            label: item.make,
            value: item.make,
          })),
          nextPageParam: null,
        };
      },
    },
  },
  {
    label: "Model Name",
    dataField: "model_name",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "Model Number",
    dataField: "model_number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "Serial Number",
    dataField: "serial_number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "Description",
    dataField: "notes",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "Encryption Key",
    dataField: "encryption_key",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  },
  {
    label: "Current Location",
    pluralLabel: "Current Locations",
    dataField: "location_id",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      cacheKey: "storage:locations",
      loadPage: async () => {
        const items = (await LocationsAPI.getLocations(
          {},
          { include_groups: false }
        )) as { location_name: string; location_id: number }[];
        return {
          items: [
            ...(items || []).map((item) => ({
              label: item.location_name,
              value: item.location_id.toString(),
            })),
            // Meta-locations: `/locations` doesn't return them. Storage rows
            // released or destroyed via chain of custody carry the reserved
            // location_ids 1 and -1, so both filter through `location_id IN`
            // like Evidence's Location filter. Id 1 is reserved: tenant
            // provisioning inserts Released as the first `locations` row
            // (monolith-api locations.constants.ts), so it can't collide with
            // a real location. "At Parent" (0) is Evidence only.
            ...[
              LocationsMetaAPI.metaLocations.released,
              LocationsMetaAPI.metaLocations.destroyed,
            ].map((item) => ({
              label: item.location_name,
              value: item.location_id.toString(),
            })),
          ],
          nextPageParam: null,
        };
      },
    },
  },
];

/**
 * Maps a STORAGE custom field to a `FilterDefinition`. Mirrors the Evidence
 * `resolveCustomFieldFilter`, but constrains operator sets to those the storage
 * backend supports — matching the static definitions above, no inclusive
 * (>=/<=) date operators (see ENG-1315/1317 backend limitation).
 */
const resolveCustomFieldFilter = (field: CustomField): FilterDefinition => {
  const filter: FilterDefinition = {
    label: field.field_name,
    dataField: `custom_field_${field.field_id}`,
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  };

  switch (field.editor_type) {
    case "textBox":
      filter.inputType = "text";
      filter.operators = [
        Operators.Equals,
        Operators.NotEquals,
        Operators.Contains,
        Operators.DoesNotContain,
      ];
      break;
    case "dropDown":
    case "tagBox":
      filter.inputType = "multiselect";
      filter.operators = [Operators.In, Operators.NIn];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    case "dateBox":
      filter.inputType = "datetime";
      filter.resolution = "day";
      // No Equals/NotEquals — see the note on the static date filters above.
      filter.operators = [
        Operators.GreaterThan,
        Operators.LessThan,
        Operators.Between,
      ];
      break;
    default:
      break;
  }

  return filter;
};

/**
 * Returns the storage filter definitions with dynamic custom-field filter
 * dimensions appended. Mirrors `useEvidenceFilterDefinitions`, scoped to the
 * STORAGE custom-field category.
 */
export const useStorageFilterDefinitions = (): FilterDefinition[] => {
  const { data: customFields } = CustomFieldsQuery.useGetCustomFields({
    category: CUSTOM_FIELD_CATEGORIES.STORAGE,
  });

  const customFieldFilters: FilterDefinition[] = useMemo(() => {
    return customFields?.data?.map(resolveCustomFieldFilter) || [];
  }, [customFields]);

  return [...StorageFilterDefinitions, ...customFieldFilters];
};
